import * as React from "react";
import { FileText, IdCard, Plus, Trash2 } from "lucide-react";
import { LocalizedField } from "../../../ui/components/LocalizedField";
import { MediaUploader } from "../../../ui/components/MediaUploader";
import type { MediaEntry } from "../../../ui/components/MediaUploader";
import { Callout } from "../../../ui/components/Callout";
import { YoutubeIcon } from "../../../ui/components/Icon";
import { captureVideoThumbnail, captureVideoThumbnailFromUrl } from "../../lib/media/captureVideoThumbnail";
import {
  getYoutubeThumbnailUrl,
  getYoutubeVideoId,
  isYoutubeUrl,
} from "../../lib/media/youtube";
import {
  updateCertificationTraining,
  requestTrainingVideoUpload,
  requestTrainingThumbnailUpload,
  fetchTrainingForEdit,
} from "../../lib/api/certifications";
import type { CertificationTraining } from "../../lib/api/certifications";
import { batchPatchLocalizationDefinitions } from "../../lib/api/localization";
import { suppressCertificationModuleRemoteRevision } from "../../lib/i18n/useCertificationModuleRemoteRevision";
import { useI18n } from "../../lib/i18n";
import { suppressCertificationLocRemoteRevision } from "../../lib/i18n/useCertificationLocLanguages";
import {
  ensureLocMigrationDirty,
  locDraftFromStored,
  newId,
  questionChoiceKey,
  trainingTitleKey,
  trainingSectionKey,
  trainingTranscriptKey,
  trainingVideoCommentaryKey,
  type LocFieldDraft,
} from "../../lib/i18n/contentKeys";
import {
  collectDirtyUpdates,
  emptyQuestionDraft,
  findDuplicateQuestionTextId,
  getLocDisplayValue,
  hydrateTrainingDraft,
  questionsToApiPayload,
  setLocValue,
  type QuestionDraft,
} from "../../lib/i18n/trainingLocalizedDraft";
import type { Language } from "../../lib/api/localization";
import { Button } from "../../../ui/components/Button";
import { flushSync } from "react-dom";
import { CourseLessonCard } from "../certification/CourseLessonCard";
import { trainingPoster } from "./trainingPoster";
import { CoursePreviewHoverButton } from "./CoursePreviewHoverButton";
import { CourseQuestionsStrip } from "./CourseQuestionsStrip";
import * as styles from "./ModuleDetailPage.css";

export type CourseTrainingEditorState = {
  dirty: boolean;
  changeCount: number;
  saving: boolean;
  /** Resolves true when the API save succeeded and local dirties were cleared. */
  save: () => Promise<boolean>;
  reset: () => void;
};

export type TrainingTextFields = {
  title: LocFieldDraft;
  section: LocFieldDraft;
  transcript: LocFieldDraft;
  videoCommentary: LocFieldDraft;
};

export type TrainingDraftSnapshot = {
  fields: TrainingTextFields;
  saved: TrainingTextFields;
  questions: QuestionDraft[];
  savedQuestions: QuestionDraft[];
  selectedQuestionId: string | null;
};

function youtubeThumbnailForVideoUrl(videoUrl: string | null | undefined): string | undefined {
  const id = videoUrl ? getYoutubeVideoId(videoUrl) : null;
  return id ? getYoutubeThumbnailUrl(id) : undefined;
}

async function uploadTrainingVideoFile(
  accessToken: string,
  trainingId: string,
  file: File,
  failMessage: string,
): Promise<string> {
  const instructions = await requestTrainingVideoUpload(accessToken, trainingId, file.type);
  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");
  const headers: Record<string, string> = { "Content-Type": file.type };
  if (!isPresigned) {
    if (instructions.key) headers["X-Video-Key"] = instructions.key;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  }
  const res = await fetch(instructions.uploadUrl, { method: "PUT", body: file, headers });
  if (!res.ok) throw new Error(failMessage);
  return instructions.videoUrl;
}

async function uploadTrainingThumbnailFile(
  accessToken: string,
  trainingId: string,
  blob: Blob,
  failMessage: string,
): Promise<string> {
  const contentType = "image/jpeg";
  const instructions = await requestTrainingThumbnailUpload(accessToken, trainingId, contentType);
  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");
  const headers: Record<string, string> = { "Content-Type": contentType };
  if (!isPresigned) {
    if (instructions.key) headers["X-Avatar-Key"] = instructions.key;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  }
  const res = await fetch(instructions.uploadUrl, { method: "PUT", body: blob, headers });
  if (!res.ok) throw new Error(failMessage);
  return instructions.avatarUrl;
}

async function resolveTrainingThumbnailBlob(
  newItem: MediaEntry,
  pendingRef: React.MutableRefObject<Blob | null>,
): Promise<Blob | undefined> {
  if (isYoutubeUrl(newItem.url)) {
    pendingRef.current = null;
    return undefined;
  }
  if (pendingRef.current) {
    const blob = pendingRef.current;
    pendingRef.current = null;
    return blob;
  }
  if (newItem.previewUrl?.startsWith("blob:")) {
    try {
      const res = await fetch(newItem.previewUrl);
      return await res.blob();
    } catch {
      // fall through
    }
  }
  if (newItem.url && /^https?:\/\//.test(newItem.url)) {
    try {
      return await captureVideoThumbnailFromUrl(newItem.url);
    } catch {
      // fall through
    }
  }
  return undefined;
}

function fieldsFromTraining(
  training: CertificationTraining,
  defaultLang: string,
  definitions?: Record<string, string>,
): TrainingTextFields {
  return {
    title: locDraftFromStored(
      training.title,
      trainingTitleKey(training.id),
      defaultLang,
      definitions,
    ),
    section: locDraftFromStored(
      training.section,
      trainingSectionKey(training.id),
      defaultLang,
      definitions,
    ),
    transcript: locDraftFromStored(
      training.transcript,
      trainingTranscriptKey(training.id),
      defaultLang,
      definitions,
    ),
    videoCommentary: locDraftFromStored(
      training.videoCommentary,
      trainingVideoCommentaryKey(training.id),
      defaultLang,
      definitions,
    ),
  };
}

function clearDirty(fields: TrainingTextFields): TrainingTextFields {
  return {
    title: { ...fields.title, dirtyLangs: [] },
    section: { ...fields.section, dirtyLangs: [] },
    transcript: { ...fields.transcript, dirtyLangs: [] },
    videoCommentary: { ...fields.videoCommentary, dirtyLangs: [] },
  };
}

function clearQuestionDirties(questions: QuestionDraft[]): QuestionDraft[] {
  return questions.map((q) => ({
    ...q,
    text: { ...q.text, dirtyLangs: [] },
    choices: q.choices.map((c) => ({
      ...c,
      loc: { ...c.loc, dirtyLangs: [] },
    })),
  }));
}

function questionsStructureSig(questions: QuestionDraft[]): string {
  // Use loc keys (not ephemeral choice.id) so soft-refresh rehydrates that
  // regenerate ids for the same content keys do not look like structure edits.
  return questions
    .map(
      (q, i) =>
        `${q.id}@${i}:${q.correctChoiceIndex}:${q.choices.map((c) => c.loc.key).join(",")}`,
    )
    .join("|");
}

function countQuestionLocDirties(questions: QuestionDraft[]): number {
  let n = 0;
  for (const q of questions) {
    if (q.text.dirtyLangs.length > 0) n += 1;
    for (const c of q.choices) {
      if (c.loc.dirtyLangs.length > 0) n += 1;
    }
  }
  return n;
}

export function isQuestionsDraftDirty(
  questions: QuestionDraft[],
  savedQuestions: QuestionDraft[],
): boolean {
  return (
    questionsStructureSig(questions) !== questionsStructureSig(savedQuestions)
    || countQuestionLocDirties(questions) > 0
  );
}

function markNonEmptyLocDirty(loc: LocFieldDraft, defaultLang: string): LocFieldDraft {
  const v = loc.valuesByLang[defaultLang]?.trim() ?? "";
  if (!v || loc.dirtyLangs.includes(defaultLang)) return loc;
  return { ...loc, dirtyLangs: [...loc.dirtyLangs, defaultLang] };
}

function questionHasUnsaved(
  question: QuestionDraft,
  saved: QuestionDraft | undefined,
): boolean {
  if (!saved) return true;
  if (question.correctChoiceIndex !== saved.correctChoiceIndex) return true;
  if (
    question.choices.map((c) => c.loc.key).join(",")
    !== saved.choices.map((c) => c.loc.key).join(",")
  ) {
    return true;
  }
  if (question.text.dirtyLangs.length > 0) return true;
  return question.choices.some((c) => c.loc.dirtyLangs.length > 0);
}

/** Merge one loc field from definitions; keep the same reference when the value is unchanged. */
function mergeLocFieldFromDefinitions(
  field: LocFieldDraft,
  lang: string,
  definitions: Record<string, string>,
): LocFieldDraft {
  if (field.dirtyLangs.includes(lang)) return field;
  const nextVal = definitions[field.key] ?? field.valuesByLang[lang] ?? "";
  if ((field.valuesByLang[lang] ?? "") === nextVal) return field;
  return {
    ...field,
    valuesByLang: {
      ...field.valuesByLang,
      [lang]: nextVal,
    },
  };
}

function mergeLanguageDefinitions(
  fields: TrainingTextFields,
  lang: string,
  definitions: Record<string, string>,
): TrainingTextFields {
  const title = mergeLocFieldFromDefinitions(fields.title, lang, definitions);
  const section = mergeLocFieldFromDefinitions(fields.section, lang, definitions);
  const transcript = mergeLocFieldFromDefinitions(fields.transcript, lang, definitions);
  const videoCommentary = mergeLocFieldFromDefinitions(
    fields.videoCommentary,
    lang,
    definitions,
  );
  if (
    title === fields.title
    && section === fields.section
    && transcript === fields.transcript
    && videoCommentary === fields.videoCommentary
  ) {
    return fields;
  }
  return { title, section, transcript, videoCommentary };
}

function mergeQuestionLanguageDefinitions(
  questions: QuestionDraft[],
  lang: string,
  definitions: Record<string, string>,
): QuestionDraft[] {
  let anyChanged = false;
  const next = questions.map((q) => {
    const text = mergeLocFieldFromDefinitions(q.text, lang, definitions);
    let choicesChanged = false;
    const choices = q.choices.map((c) => {
      const loc = mergeLocFieldFromDefinitions(c.loc, lang, definitions);
      if (loc === c.loc) return c;
      choicesChanged = true;
      return { ...c, loc };
    });
    if (text === q.text && !choicesChanged) return q;
    anyChanged = true;
    return { ...q, text, choices };
  });
  return anyChanged ? next : questions;
}

/** Collect content keys whose displayed value changed for a clean (non-dirty) field. */
function collectMergedFlashKeys(
  before: LocFieldDraft,
  after: LocFieldDraft,
  lang: string,
): string[] {
  if (before.dirtyLangs.includes(lang)) return [];
  if (before === after) return [];
  const prevVal = before.valuesByLang[lang] ?? "";
  const nextVal = after.valuesByLang[lang] ?? "";
  return prevVal !== nextVal ? [before.key] : [];
}

/**
 * Soft-refresh flash keys: only keys whose default-lang value actually changed
 * (or newly appeared), plus the choice that becomes the correct answer when
 * `correctChoiceIndex` changes remotely.
 */
function collectQuestionValueFlashKeys(
  before: QuestionDraft[],
  after: QuestionDraft[],
  defaultLang: string,
): string[] {
  const flash: string[] = [];
  const beforeById = new Map(before.map((q) => [q.id, q]));
  for (const aq of after) {
    const bq = beforeById.get(aq.id);
    if (!bq) {
      flash.push(aq.text.key, ...aq.choices.map((c) => c.loc.key));
      continue;
    }
    const prevText = bq.text.valuesByLang[defaultLang] ?? "";
    const nextText = aq.text.valuesByLang[defaultLang] ?? "";
    if (prevText !== nextText) flash.push(aq.text.key);

    const beforeChoiceByKey = new Map(bq.choices.map((c) => [c.loc.key, c]));
    aq.choices.forEach((ac, ci) => {
      const bc =
        beforeChoiceByKey.get(ac.loc.key)
        ?? (bq.choices[ci]?.loc.key === ac.loc.key ? bq.choices[ci] : undefined);
      if (!bc) {
        flash.push(ac.loc.key);
        return;
      }
      const prevVal = bc.loc.valuesByLang[defaultLang] ?? "";
      const nextVal = ac.loc.valuesByLang[defaultLang] ?? "";
      if (prevVal !== nextVal) flash.push(ac.loc.key);
    });

    // Correct-answer radio moved → shimmer the newly marked choice field.
    if (bq.correctChoiceIndex !== aq.correctChoiceIndex) {
      const newlyCorrect = aq.choices[aq.correctChoiceIndex];
      if (newlyCorrect) flash.push(newlyCorrect.loc.key);
    }
  }
  return flash;
}

export async function persistTrainingTextDraft(opts: {
  accessToken: string;
  training: CertificationTraining;
  fields: TrainingTextFields;
  questions: QuestionDraft[];
  questionsDirty: boolean;
  defaultLang: string;
}): Promise<CertificationTraining> {
  const { accessToken, training, fields, defaultLang, questionsDirty } = opts;
  const title = ensureLocMigrationDirty(fields.title, training.title, defaultLang);
  const section = ensureLocMigrationDirty(fields.section, training.section, defaultLang);
  const transcript = ensureLocMigrationDirty(
    fields.transcript,
    training.transcript,
    defaultLang,
  );
  const videoCommentary = ensureLocMigrationDirty(
    fields.videoCommentary,
    training.videoCommentary,
    defaultLang,
  );

  const questionsForSave = opts.questions.map((q, i) => ({
    ...q,
    sortOrder: i,
    text: markNonEmptyLocDirty(q.text, defaultLang),
    choices: q.choices.map((c) => ({
      ...c,
      loc: markNonEmptyLocDirty(c.loc, defaultLang),
    })),
  }));

  const locDrafts: LocFieldDraft[] = [title, section, transcript, videoCommentary];
  if (questionsDirty) {
    for (const q of questionsForSave) {
      locDrafts.push(q.text);
      for (const c of q.choices) locDrafts.push(c.loc);
    }
  }

  const updates = collectDirtyUpdates(locDrafts).filter(
    (update) => Object.keys(update.set).length > 0,
  );
  if (updates.length > 0) {
    suppressCertificationLocRemoteRevision(2500);
    await batchPatchLocalizationDefinitions(accessToken, {
      context: "certification",
      updates,
    });
  }

  suppressCertificationModuleRemoteRevision(2500);
  const updated = await updateCertificationTraining(accessToken, training.id, {
    title: title.key,
    section: getLocDisplayValue(section, defaultLang, defaultLang).trim()
      ? section.key
      : null,
    transcript: getLocDisplayValue(transcript, defaultLang, defaultLang).trim()
      ? transcript.key
      : null,
    videoCommentary: getLocDisplayValue(videoCommentary, defaultLang, defaultLang).trim()
      ? videoCommentary.key
      : null,
    ...(questionsDirty
      ? { questions: questionsToApiPayload(questionsForSave, defaultLang) }
      : {}),
  });

  return updated;
}

export function CourseTrainingDetail({
  training,
  index,
  accessToken,
  onSaved,
  onStateChange,
  draft,
  onDraftChange,
  active = true,
  languages,
  defaultLang,
  defsByLang,
  locReady,
  remoteRevision = 0,
  trainingRemoteRevision = 0,
  ensureLangDefinitions,
  selectedQuestionId: selectedQuestionIdProp,
  onSelectedQuestionIdChange,
}: {
  training: CertificationTraining;
  index: number;
  accessToken: string;
  onSaved: (training: CertificationTraining) => void;
  onStateChange?: (state: CourseTrainingEditorState | null) => void;
  /** Restored draft when re-selecting a training with unsaved edits. */
  draft?: TrainingDraftSnapshot | null;
  onDraftChange?: (
    trainingId: string,
    snapshot: TrainingDraftSnapshot,
    changeCount: number,
  ) => void;
  /** When false, skip editor/draft side-effects (used for outgoing slide panels). */
  active?: boolean;
  languages: Language[];
  defaultLang: string;
  defsByLang: Record<string, Record<string, string>>;
  locReady: boolean;
  /** Bumps when localization defs arrive via websocket. */
  remoteRevision?: number;
  /** Bumps when training/question structure arrives via websocket. */
  trainingRemoteRevision?: number;
  ensureLangDefinitions: (lang: string) => Promise<Record<string, string>>;
  selectedQuestionId?: string | null;
  onSelectedQuestionIdChange?: (questionId: string | null) => void;
}) {
  const { t } = useI18n();
  const [fields, setFields] = React.useState<TrainingTextFields>(() =>
    draft?.fields ?? fieldsFromTraining(training, "en"),
  );
  const [savedFields, setSavedFields] = React.useState<TrainingTextFields>(() =>
    draft?.saved ?? fieldsFromTraining(training, "en"),
  );
  const [questions, setQuestions] = React.useState<QuestionDraft[]>(
    () => draft?.questions ?? [],
  );
  const [savedQuestions, setSavedQuestions] = React.useState<QuestionDraft[]>(
    () => draft?.savedQuestions ?? [],
  );
  const [selectedQuestionIdInternal, setSelectedQuestionIdInternal] = React.useState<string | null>(
    () => selectedQuestionIdProp ?? draft?.selectedQuestionId ?? null,
  );
  const selectedQuestionId = selectedQuestionIdProp ?? selectedQuestionIdInternal;
  const setSelectedQuestionId = React.useCallback(
    (next: string | null | ((prev: string | null) => string | null)) => {
      const prev = selectedQuestionIdProp ?? selectedQuestionIdInternal;
      const resolved = typeof next === "function" ? next(prev) : next;
      if (selectedQuestionIdProp === undefined) {
        setSelectedQuestionIdInternal(resolved);
      }
      onSelectedQuestionIdChange?.(resolved);
    },
    [onSelectedQuestionIdChange, selectedQuestionIdProp, selectedQuestionIdInternal],
  );
  const [questionsLoading, setQuestionsLoading] = React.useState(!draft?.questions);
  const [fieldLang, setFieldLang] = React.useState<Record<string, string>>({});
  const [videoUrl, setVideoUrl] = React.useState<string | null>(training.videoUrl ?? null);
  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>(
    training.thumbnailUrl ?? null,
  );
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [videoError, setVideoError] = React.useState<string | null>(null);
  const [flashKeys, setFlashKeys] = React.useState<Record<string, number>>({});
  const pendingThumbnailBlobRef = React.useRef<Blob | null>(null);
  const locHydrationRef = React.useRef("");
  const questionsLoadedForRef = React.useRef<string | null>(
    draft?.questions ? training.id : null,
  );
  /** After a successful save, ignore draft reports until local changeCount hits 0. */
  const suppressDirtyReportRef = React.useRef(false);
  const fieldsRef = React.useRef(fields);
  const questionsRef = React.useRef(questions);
  const savedFieldsRef = React.useRef(savedFields);
  const savedQuestionsRef = React.useRef(savedQuestions);
  const selectedQuestionIdRef = React.useRef(selectedQuestionId);
  const videoUrlRef = React.useRef(videoUrl);
  const thumbnailUrlRef = React.useRef(thumbnailUrl);
  fieldsRef.current = fields;
  questionsRef.current = questions;
  savedFieldsRef.current = savedFields;
  savedQuestionsRef.current = savedQuestions;
  selectedQuestionIdRef.current = selectedQuestionId;
  videoUrlRef.current = videoUrl;
  thumbnailUrlRef.current = thumbnailUrl;


  React.useEffect(() => {
    if (selectedQuestionIdProp === undefined) return;
    setSelectedQuestionIdInternal(selectedQuestionIdProp);
  }, [selectedQuestionIdProp]);

  React.useEffect(() => {
    if (draft || !locReady) return;
    const hydrationKey = [
      training.id,
      training.title,
      training.section ?? "",
      training.transcript ?? "",
      training.videoCommentary ?? "",
      defaultLang,
    ].join(":");
    if (locHydrationRef.current === hydrationKey) return;
    const next = fieldsFromTraining(training, defaultLang, defsByLang[defaultLang]);
    locHydrationRef.current = hydrationKey;
    setFields(next);
    setSavedFields(next);
    setFieldLang({});
    setSaveError(null);
  }, [
    training.id,
    training.title,
    training.section,
    training.transcript,
    training.videoCommentary,
    draft,
    locReady,
    defaultLang,
    defsByLang,
  ]);

  React.useEffect(() => {
    // Restore in-progress draft when re-selecting this training; otherwise load from API.
    // Intentionally keyed on training.id (not draft identity) to avoid refetching on each edit.
    if (draft?.questions) {
      setQuestions(draft.questions);
      setSavedQuestions(draft.savedQuestions);
      setSelectedQuestionId(draft.selectedQuestionId);
      setQuestionsLoading(false);
      questionsLoadedForRef.current = training.id;
      return;
    }
    if (!locReady) return;

    let cancelled = false;
    const soft = questionsLoadedForRef.current === training.id && questionsRef.current.length > 0;
    if (!soft) setQuestionsLoading(true);
    void fetchTrainingForEdit(accessToken, training.id)
      .then((full) => {
        if (cancelled) return;
        const hydrated = hydrateTrainingDraft({
          trainingId: training.id,
          title: full.title,
          section: full.section,
          transcript: full.transcript,
          questions: full.questions,
          defaultLang,
          definitions: defsByLang[defaultLang],
        });
        const nextQuestions = clearQuestionDirties(hydrated.questions);
        setQuestions(nextQuestions);
        setSavedQuestions(nextQuestions);
        questionsLoadedForRef.current = training.id;
        setSelectedQuestionId((prev) => {
          if (prev && nextQuestions.some((q) => q.id === prev)) return prev;
          if (
            selectedQuestionIdProp
            && nextQuestions.some((q) => q.id === selectedQuestionIdProp)
          ) {
            return selectedQuestionIdProp;
          }
          return nextQuestions[0]?.id ?? null;
        });
      })
      .catch(() => {
        if (cancelled) return;
        if (!soft) {
          setQuestions([]);
          setSavedQuestions([]);
          setSelectedQuestionId(null);
          questionsLoadedForRef.current = null;
        }
      })
      .finally(() => {
        if (!cancelled) setQuestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draft snapshot only applied on training switch; defs read at fetch time
  }, [accessToken, training.id, locReady, defaultLang]);

  // Soft-merge remote localization updates into clean fields; flash changed keys.
  React.useEffect(() => {
    if (!active || !locReady || remoteRevision <= 0) return;

    const flash: string[] = [];
    for (const [lang, definitions] of Object.entries(defsByLang)) {
      const beforeFields = fieldsRef.current;
      const afterFields = mergeLanguageDefinitions(beforeFields, lang, definitions);
      flash.push(
        ...collectMergedFlashKeys(beforeFields.title, afterFields.title, lang),
        ...collectMergedFlashKeys(beforeFields.section, afterFields.section, lang),
        ...collectMergedFlashKeys(beforeFields.transcript, afterFields.transcript, lang),
        ...collectMergedFlashKeys(
          beforeFields.videoCommentary,
          afterFields.videoCommentary,
          lang,
        ),
      );
      setFields((prev) => mergeLanguageDefinitions(prev, lang, definitions));
      setSavedFields((prev) => mergeLanguageDefinitions(prev, lang, definitions));

      const beforeQs = questionsRef.current;
      const afterQs = mergeQuestionLanguageDefinitions(beforeQs, lang, definitions);
      beforeQs.forEach((q, i) => {
        const aq = afterQs[i];
        if (!aq) return;
        flash.push(...collectMergedFlashKeys(q.text, aq.text, lang));
        q.choices.forEach((c, ci) => {
          const ac = aq.choices[ci];
          if (ac) flash.push(...collectMergedFlashKeys(c.loc, ac.loc, lang));
        });
      });
      setQuestions((prev) => mergeQuestionLanguageDefinitions(prev, lang, definitions));
      setSavedQuestions((prev) => mergeQuestionLanguageDefinitions(prev, lang, definitions));
    }

    if (flash.length > 0) {
      setFlashKeys((prev) => {
        const next = { ...prev };
        for (const key of flash) next[key] = (prev[key] ?? 0) + 1;
        return next;
      });
    }
    // Only react to localization websocket revision bumps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteRevision]);

  // Soft-refresh training media + quiz structure from training WS.
  React.useEffect(() => {
    if (!active || trainingRemoteRevision <= 0) return;

    let cancelled = false;
    void fetchTrainingForEdit(accessToken, training.id)
      .then((full) => {
        if (cancelled) return;

        const nextVideo = full.videoUrl ?? null;
        const nextThumb = full.thumbnailUrl ?? null;
        const mediaChanged =
          (videoUrlRef.current ?? null) !== nextVideo
          || (thumbnailUrlRef.current ?? null) !== nextThumb;
        if (mediaChanged) {
          setVideoUrl(nextVideo);
          setThumbnailUrl(nextThumb);
          setVideoError(null);
          pendingThumbnailBlobRef.current = null;
          const { questions: _remoteQuestions, ...trainingRow } = full;
          onSaved({
            ...trainingRow,
            videoUrl: nextVideo,
            thumbnailUrl: nextThumb,
          });
        }

        // Adopt remote quiz structure only when local questions are clean.
        const structureDirty =
          questionsStructureSig(questionsRef.current)
          !== questionsStructureSig(savedQuestionsRef.current);
        const locDirty = countQuestionLocDirties(questionsRef.current) > 0;
        if (structureDirty || locDirty || !locReady) return;

        const hydrated = hydrateTrainingDraft({
          trainingId: training.id,
          title: full.title,
          section: full.section,
          transcript: full.transcript,
          questions: full.questions,
          defaultLang,
          definitions: defsByLang[defaultLang],
        });
        const nextQuestions = clearQuestionDirties(hydrated.questions);
        const prevQuestions = questionsRef.current;
        const prevSig = questionsStructureSig(prevQuestions);
        const nextSig = questionsStructureSig(nextQuestions);
        const valueFlash = collectQuestionValueFlashKeys(
          prevQuestions,
          nextQuestions,
          defaultLang,
        );
        if (prevSig !== nextSig || valueFlash.length > 0) {
          setQuestions(nextQuestions);
          setSavedQuestions(nextQuestions);
        }
        if (valueFlash.length > 0) {
          setFlashKeys((prev) => {
            const next = { ...prev };
            for (const key of valueFlash) next[key] = (prev[key] ?? 0) + 1;
            return next;
          });
        }
        setSelectedQuestionId((prev) => {
          if (prev && nextQuestions.some((q) => q.id === prev)) return prev;
          return nextQuestions[0]?.id ?? null;
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // Only react to training-structure websocket revision bumps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingRemoteRevision]);

  React.useEffect(() => {
    setVideoUrl(training.videoUrl ?? null);
    setThumbnailUrl(training.thumbnailUrl ?? null);
    setVideoError(null);
    pendingThumbnailBlobRef.current = null;
  }, [training.id, training.videoUrl, training.thumbnailUrl]);

  const textChangeCount = [
    fields.title,
    fields.section,
    fields.transcript,
    fields.videoCommentary,
  ].filter((field) => field.dirtyLangs.length > 0).length;
  const questionsStructureChanged =
    questionsStructureSig(questions) !== questionsStructureSig(savedQuestions);
  const questionLocChangeCount = countQuestionLocDirties(questions);
  const changeCount =
    textChangeCount
    + questionLocChangeCount
    + (questionsStructureChanged ? 1 : 0);
  const questionsDirty = questionsStructureChanged || questionLocChangeCount > 0;
  const dirty = changeCount > 0;

  const unsavedQuestionIds = React.useMemo(() => {
    const savedById = new Map(savedQuestions.map((q) => [q.id, q]));
    const set = new Set<string>();
    for (const q of questions) {
      if (questionHasUnsaved(q, savedById.get(q.id))) {
        set.add(q.id);
      }
    }
    // If only order changed, mark every circle that moved relative to saved.
    if (questionsStructureChanged) {
      const savedOrder = savedQuestions.map((q) => q.id);
      questions.forEach((q, i) => {
        if (savedOrder[i] !== q.id) set.add(q.id);
      });
    }
    return set;
  }, [questions, savedQuestions, questionsStructureChanged]);

  React.useEffect(() => {
    if (!active) return;
    if (suppressDirtyReportRef.current) {
      if (changeCount === 0) {
        suppressDirtyReportRef.current = false;
        onDraftChange?.(
          training.id,
          {
            fields,
            saved: savedFields,
            questions,
            savedQuestions,
            selectedQuestionId,
          },
          0,
        );
      } else {
      }
      return;
    }
    onDraftChange?.(
      training.id,
      {
        fields,
        saved: savedFields,
        questions,
        savedQuestions,
        selectedQuestionId,
      },
      changeCount,
    );
  }, [
    active,
    training.id,
    fields,
    savedFields,
    questions,
    savedQuestions,
    selectedQuestionId,
    changeCount,
    questionsStructureChanged,
    onDraftChange,
  ]);

  const patchField = React.useCallback((
    name: keyof TrainingTextFields,
    lang: string,
    value: string,
  ) => {
    suppressDirtyReportRef.current = false;
    setFields((prev) => ({ ...prev, [name]: setLocValue(prev[name], lang, value) }));
    setSaveError(null);
  }, []);

  const reset = React.useCallback(() => {
    setFields(savedFields);
    setQuestions(savedQuestions);
    setSelectedQuestionId((prev) => {
      if (prev && savedQuestions.some((q) => q.id === prev)) return prev;
      return savedQuestions[0]?.id ?? null;
    });
    setSaveError(null);
  }, [savedFields, savedQuestions]);

  const activeLangFor = React.useCallback(
    (key: string) => fieldLang[key] ?? defaultLang,
    [fieldLang, defaultLang],
  );

  const onFieldLangChange = React.useCallback(
    (key: string, lang: string) => {
      setFieldLang((prev) => ({ ...prev, [key]: lang }));
      void ensureLangDefinitions(lang).then((definitions) => {
        setFields((prev) => mergeLanguageDefinitions(prev, lang, definitions));
        setSavedFields((prev) => mergeLanguageDefinitions(prev, lang, definitions));
        setQuestions((prev) => mergeQuestionLanguageDefinitions(prev, lang, definitions));
        setSavedQuestions((prev) => mergeQuestionLanguageDefinitions(prev, lang, definitions));
      });
    },
    [ensureLangDefinitions],
  );

  const handleAddQuestion = React.useCallback(() => {
    suppressDirtyReportRef.current = false;
    const next = emptyQuestionDraft(questions.length, defaultLang);
    setQuestions((prev) => [...prev, next]);
    setSelectedQuestionId(next.id);
    setSaveError(null);
  }, [questions.length, defaultLang, setSelectedQuestionId]);

  const handleReorderQuestions = React.useCallback((orderedIds: string[]) => {
    suppressDirtyReportRef.current = false;
    setQuestions((prev) => {
      const byId = new Map(prev.map((q) => [q.id, q]));
      return orderedIds
        .map((id, i) => {
          const q = byId.get(id);
          return q ? { ...q, sortOrder: i } : null;
        })
        .filter((q): q is QuestionDraft => q != null);
    });
    setSaveError(null);
  }, []);

  const handleDeleteQuestion = React.useCallback((questionId: string) => {
    suppressDirtyReportRef.current = false;
    setQuestions((prev) => {
      const next = prev
        .filter((q) => q.id !== questionId)
        .map((q, i) => ({ ...q, sortOrder: i }));
      setSelectedQuestionId((current) => {
        if (current !== questionId) return current;
        return next[0]?.id ?? null;
      });
      return next;
    });
    setSaveError(null);
  }, [setSelectedQuestionId]);

  const updateQuestionAt = React.useCallback(
    (questionId: string, updater: (q: QuestionDraft) => QuestionDraft) => {
      suppressDirtyReportRef.current = false;
      setQuestions((prev) =>
        prev.map((q) => (q.id === questionId ? updater(q) : q)),
      );
      setSaveError(null);
    },
    [],
  );

  const save = React.useCallback(async (): Promise<boolean> => {
    // Always read latest editor state from refs — avoids stale save() closures
    // when the floating bar captured an older trainingEditor.save.
    const fieldsNow = fieldsRef.current;
    const questionsNow = questionsRef.current;
    const savedQuestionsNow = savedQuestionsRef.current;
    const selectedQuestionIdNow = selectedQuestionIdRef.current;
    const structureDirty =
      questionsStructureSig(questionsNow) !== questionsStructureSig(savedQuestionsNow);
    const questionsDirtyNow =
      structureDirty || countQuestionLocDirties(questionsNow) > 0;
    const changeCountNow =
      [
        fieldsNow.title,
        fieldsNow.section,
        fieldsNow.transcript,
        fieldsNow.videoCommentary,
      ].filter((field) => field.dirtyLangs.length > 0).length
      + countQuestionLocDirties(questionsNow)
      + (structureDirty ? 1 : 0);


    if (!getLocDisplayValue(fieldsNow.title, defaultLang, defaultLang).trim()) {
      setSaveError(t("pro.system.certification.training.validation.titleRequired"));
      return false;
    }
    for (const q of questionsNow) {
      if (!getLocDisplayValue(q.text, defaultLang, defaultLang).trim()) {
        setSaveError(t("pro.system.certification.training.validation.questionTextRequired"));
        setSelectedQuestionId(q.id);
        return false;
      }
      for (const c of q.choices) {
        if (!getLocDisplayValue(c.loc, defaultLang, defaultLang).trim()) {
          setSaveError(t("pro.system.certification.training.validation.choiceTextRequired"));
          setSelectedQuestionId(q.id);
          return false;
        }
      }
    }
    const duplicateQuestionId = findDuplicateQuestionTextId(questionsNow, defaultLang);
    if (duplicateQuestionId) {
      setSaveError(t("pro.system.certification.training.validation.duplicateQuestionText"));
      setSelectedQuestionId(duplicateQuestionId);
      return false;
    }
    setSaving(true);
    setSaveError(null);
    // Suppress our own localization WS echo so we don't remount/refetch mid-clear.
    suppressCertificationLocRemoteRevision(2500);
    try {
      const updated = await persistTrainingTextDraft({
        accessToken,
        training,
        fields: fieldsNow,
        questions: questionsNow,
        questionsDirty: questionsDirtyNow,
        defaultLang,
      });

      const nextSaved = clearDirty(fieldsNow);
      const nextQuestions = clearQuestionDirties(
        questionsNow.map((q, i) => ({ ...q, sortOrder: i })),
      );
      locHydrationRef.current = [
        training.id,
        updated.title,
        updated.section ?? "",
        updated.transcript ?? "",
        updated.videoCommentary ?? "",
        defaultLang,
      ].join(":");
      suppressDirtyReportRef.current = true;
      // Flush before notifying parent so a remount cannot read stale dirty state.
      flushSync(() => {
        setFields(nextSaved);
        setSavedFields(nextSaved);
        setQuestions(nextQuestions);
        setSavedQuestions(nextQuestions);
      });
      onDraftChange?.(
        training.id,
        {
          fields: nextSaved,
          saved: nextSaved,
          questions: nextQuestions,
          savedQuestions: nextQuestions,
          selectedQuestionId: selectedQuestionIdNow,
        },
        0,
      );
      onSaved({
        ...updated,
        videoUrl: updated.videoUrl ?? videoUrlRef.current,
        thumbnailUrl: updated.thumbnailUrl ?? thumbnailUrlRef.current,
      });
      return true;
    } catch (err: unknown) {
      suppressDirtyReportRef.current = false;
      const message =
        err instanceof Error
          ? err.message
          : t("pro.system.certification.training.error.save");
      setSaveError(message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    training,
    accessToken,
    defaultLang,
    onDraftChange,
    onSaved,
    setSelectedQuestionId,
    t,
  ]);

  const saveRef = React.useRef(save);
  saveRef.current = save;
  const resetRef = React.useRef(reset);
  resetRef.current = reset;

  React.useEffect(() => {
    if (!active) return;
    // Stable save/reset wrappers always call the latest ref — prevents the
    // floating bar from invoking a stale closure from an older render.
    onStateChange?.({
      dirty,
      changeCount,
      saving,
      save: () => saveRef.current(),
      reset: () => resetRef.current(),
    });
  }, [active, dirty, changeCount, saving, onStateChange]);

  // Only clear the parent editor handle on true deactivate/unmount — not when
  // dirty/saving churns (that was briefly nulling the editor between publishes).
  React.useEffect(() => {
    if (!active) {
      onStateChange?.(null);
      return;
    }
    return () => onStateChange?.(null);
  }, [active, onStateChange]);
  const videoItems: MediaEntry[] = React.useMemo(() => {
    if (!videoUrl) return [];
    return [
      {
        id: training.id,
        url: videoUrl,
        previewUrl:
          thumbnailUrl
          ?? youtubeThumbnailForVideoUrl(videoUrl)
          ?? undefined,
      },
    ];
  }, [videoUrl, thumbnailUrl, training.id]);

  const uploadVideoFile = React.useCallback(
    async (file: File): Promise<{ url: string; previewUrl?: string }> => {
      let previewUrl: string | undefined;
      try {
        const thumbnailBlob = await captureVideoThumbnail(file);
        pendingThumbnailBlobRef.current = thumbnailBlob;
        previewUrl = URL.createObjectURL(thumbnailBlob);
      } catch {
        pendingThumbnailBlobRef.current = null;
      }
      const uploadedUrl = await uploadTrainingVideoFile(
        accessToken,
        training.id,
        file,
        t("pro.system.certification.training.error.uploadVideo"),
      );
      return { url: uploadedUrl, previewUrl };
    },
    [accessToken, training.id, t],
  );

  const handleVideoApply = React.useCallback(
    async (newItems: MediaEntry[]) => {
      const newItem = newItems[0];
      if (!newItem?.url) return;
      try {
        setVideoError(null);
        const ytThumb = youtubeThumbnailForVideoUrl(newItem.url);
        let nextThumb: string | undefined = ytThumb;
        if (!ytThumb) {
          const thumbnailBlob = await resolveTrainingThumbnailBlob(
            newItem,
            pendingThumbnailBlobRef,
          );
          if (thumbnailBlob) {
            nextThumb = await uploadTrainingThumbnailFile(
              accessToken,
              training.id,
              thumbnailBlob,
              t("pro.system.certification.training.error.uploadThumbnail"),
            );
          }
        } else {
          pendingThumbnailBlobRef.current = null;
        }
        suppressCertificationModuleRemoteRevision(2500);
        const updated = await updateCertificationTraining(accessToken, training.id, {
          videoUrl: newItem.url,
          ...(nextThumb ? { thumbnailUrl: nextThumb } : {}),
        });
        const persistedThumb = updated.thumbnailUrl ?? nextThumb ?? null;
        setVideoUrl(updated.videoUrl ?? newItem.url);
        setThumbnailUrl(persistedThumb);
        onSaved({ ...updated, thumbnailUrl: persistedThumb });
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : t("pro.system.certification.training.error.saveVideo");
        setVideoError(message);
        throw err;
      }
    },
    [accessToken, training.id, onSaved, t],
  );

  const handleVideoRemove = React.useCallback(async () => {
    try {
      setVideoError(null);
      suppressCertificationModuleRemoteRevision(2500);
      const updated = await updateCertificationTraining(accessToken, training.id, {
        videoUrl: null,
        thumbnailUrl: null,
      });
      setVideoUrl(updated.videoUrl ?? null);
      setThumbnailUrl(updated.thumbnailUrl ?? null);
      onSaved(updated);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t("pro.system.certification.training.error.removeVideo");
      setVideoError(message);
    }
  }, [accessToken, training.id, onSaved, t]);

  const poster = thumbnailUrl || trainingPoster({ ...training, videoUrl, thumbnailUrl }, index);
  const youtube = isYoutubeUrl(videoUrl);
  const titleLang = activeLangFor(fields.title.key);
  const sectionLang = activeLangFor(fields.section.key);
  const transcriptLang = activeLangFor(fields.transcript.key);
  const videoCommentaryLang = activeLangFor(fields.videoCommentary.key);
  const titleDisplay = getLocDisplayValue(fields.title, titleLang, defaultLang);
  const sectionDisplay = getLocDisplayValue(fields.section, sectionLang, defaultLang);
  const selectedQuestionIndex = questions.findIndex((q) => q.id === selectedQuestionId);
  const selectedQuestion =
    selectedQuestionIndex >= 0 ? questions[selectedQuestionIndex] : null;

  return (
    <div className={styles.trainingDetail}>
      <div className={styles.trainingDetailHeader}>
        <div className={styles.trainingDetailMedia}>
          {poster ? (
            <img src={poster} alt="" className={styles.trainingDetailImg} />
          ) : videoUrl && !youtube ? (
            <video
              src={videoUrl}
              className={styles.trainingDetailImg}
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <div className={styles.trainingDetailPlaceholder}>
              <FileText size={28} aria-hidden />
            </div>
          )}
          {youtube ? (
            <span className={styles.trainingDetailYoutube} aria-hidden>
              <YoutubeIcon size={14} />
            </span>
          ) : null}
        </div>
        <div className={styles.trainingDetailInfo}>
          <h3 className={styles.trainingDetailTitle}>
            {titleDisplay.trim() || t("shared.microgrid.untitled")}
          </h3>
          <div className={styles.trainingDetailBadges}>
            <span className={styles.trainingDetailStepBadge}>
              {t("pro.system.modules.training.col.step")} {index + 1}
            </span>
            {sectionDisplay.trim() ? (
              <span className={styles.trainingDetailSectionBadge}>{sectionDisplay.trim()}</span>
            ) : null}
          </div>
        </div>
      </div>

      {(saveError || videoError) && (
        <Callout variant="error">{saveError ?? videoError}</Callout>
      )}

      <div className={styles.trainingForm}>
        <div className={styles.titleSectionRow}>
          <LocalizedField
            label={t("pro.system.certification.training.form.title")}
            locKey={fields.title.key}
            languages={languages}
            selectedLang={titleLang}
            onSelectedLangChange={(lang) => onFieldLangChange(fields.title.key, lang)}
            value={titleDisplay}
            referenceValue={fields.title.valuesByLang[defaultLang]}
            onChange={(lang, value) => patchField("title", lang, value)}
            onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
            disabled={saving || !locReady}
            required
            placeholder={t("pro.system.certification.training.form.titlePlaceholder")}
            defaultLang={defaultLang}
            changed={fields.title.dirtyLangs.length > 0}
            flashKey={flashKeys[fields.title.key] ?? 0}
          />
          <LocalizedField
            label={t("pro.system.certification.training.form.section")}
            locKey={fields.section.key}
            languages={languages}
            selectedLang={sectionLang}
            onSelectedLangChange={(lang) => onFieldLangChange(fields.section.key, lang)}
            value={sectionDisplay}
            referenceValue={fields.section.valuesByLang[defaultLang]}
            onChange={(lang, value) => patchField("section", lang, value)}
            onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
            disabled={saving || !locReady}
            placeholder={t("pro.system.certification.training.form.sectionPlaceholder")}
            defaultLang={defaultLang}
            changed={fields.section.dirtyLangs.length > 0}
            flashKey={flashKeys[fields.section.key] ?? 0}
          />
        </div>

        <div className={styles.transcriptVideoRow}>
          <div className={styles.transcriptCommentaryStack}>
            <LocalizedField
              label={t("pro.system.certification.training.form.transcript")}
              locKey={fields.transcript.key}
              languages={languages}
              selectedLang={transcriptLang}
              onSelectedLangChange={(lang) => onFieldLangChange(fields.transcript.key, lang)}
              value={getLocDisplayValue(fields.transcript, transcriptLang, defaultLang)}
              referenceValue={fields.transcript.valuesByLang[defaultLang]}
              onChange={(lang, value) => patchField("transcript", lang, value)}
              onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
              disabled={saving || !locReady}
              multiline
              rows={4}
              placeholder={t("pro.system.certification.training.form.transcriptPlaceholder")}
              defaultLang={defaultLang}
              changed={fields.transcript.dirtyLangs.length > 0}
              flashKey={flashKeys[fields.transcript.key] ?? 0}
            />

            <LocalizedField
              label={t("pro.system.certification.training.form.videoCommentary")}
              locKey={fields.videoCommentary.key}
              languages={languages}
              selectedLang={videoCommentaryLang}
              onSelectedLangChange={(lang) => onFieldLangChange(fields.videoCommentary.key, lang)}
              value={getLocDisplayValue(fields.videoCommentary, videoCommentaryLang, defaultLang)}
              referenceValue={fields.videoCommentary.valuesByLang[defaultLang]}
              onChange={(lang, value) => patchField("videoCommentary", lang, value)}
              onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
              disabled={saving || !locReady}
              multiline
              rows={3}
              placeholder={t("pro.system.certification.training.form.videoCommentaryPlaceholder")}
              defaultLang={defaultLang}
              changed={fields.videoCommentary.dirtyLangs.length > 0}
              flashKey={flashKeys[fields.videoCommentary.key] ?? 0}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {t("pro.system.certification.training.video")}
            </label>
            <div className={styles.videoUploader}>
              <MediaUploader
                mediaType="video"
                allowYoutube
                items={videoItems}
                maxItems={1}
                shape="rectangle"
                aspectRatio={styles.VIDEO_ASPECT}
                uploadFile={uploadVideoFile}
                onApply={handleVideoApply}
                onStagedVideoThumbnail={(blob) => {
                  pendingThumbnailBlobRef.current = blob;
                }}
                onChange={() => {}}
                onRemove={handleVideoRemove}
                error={videoError}
                onError={setVideoError}
              />
            </div>
          </div>
        </div>

        <div className={styles.trainingPreviewsRow}>
          <span className={styles.previewIconsLabel}>
            {t("pro.system.modules.course.previewsLabel")}
          </span>
          <CoursePreviewHoverButton
            label={t("pro.system.modules.course.lessonCardPreviewLabel")}
            icon={<IdCard size={16} aria-hidden />}
            previewClassName={styles.previewPopoverLessonCard}
          >
            <div className={styles.lessonCardPreviewWrap}>
              <CourseLessonCard
                title={titleDisplay.trim() || training.title}
                section={sectionDisplay.trim() || training.section}
                videoUrl={videoUrl}
                thumbnailUrl={thumbnailUrl}
                stepNumber={index + 1}
                fallbackIndex={index}
                interactive={false}
              />
            </div>
          </CoursePreviewHoverButton>
        </div>

        <CourseQuestionsStrip
          questionIds={questions.map((q) => q.id)}
          selectedId={selectedQuestionId}
          onSelect={setSelectedQuestionId}
          onReorder={handleReorderQuestions}
          onAdd={handleAddQuestion}
          onDelete={handleDeleteQuestion}
          unsavedIds={unsavedQuestionIds}
          disabled={saving || questionsLoading || !locReady}
        />

        {questionsLoading ? (
          <p className={styles.questionEmpty}>{t("pro.orgs.loading")}</p>
        ) : selectedQuestion ? (
          <div className={styles.questionDetail}>
            <LocalizedField
              label={t("pro.system.certification.training.questions.questionN", {
                n: selectedQuestionIndex + 1,
              })}
              locKey={selectedQuestion.text.key}
              languages={languages}
              selectedLang={activeLangFor(selectedQuestion.text.key)}
              onSelectedLangChange={(lang) =>
                onFieldLangChange(selectedQuestion.text.key, lang)
              }
              value={getLocDisplayValue(
                selectedQuestion.text,
                activeLangFor(selectedQuestion.text.key),
                defaultLang,
              )}
              referenceValue={selectedQuestion.text.valuesByLang[defaultLang]}
              onChange={(lang, value) =>
                updateQuestionAt(selectedQuestion.id, (q) => ({
                  ...q,
                  text: setLocValue(q.text, lang, value),
                }))
              }
              onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
              disabled={saving || !locReady}
              placeholder={t(
                "pro.system.certification.training.questions.questionText",
              )}
              defaultLang={defaultLang}
              changed={selectedQuestion.text.dirtyLangs.length > 0}
              flashKey={flashKeys[selectedQuestion.text.key] ?? 0}
            />

            <div className={styles.questionChoices}>
              {selectedQuestion.choices.map((choice, cIndex) => (
                <div key={choice.id} className={styles.questionChoiceRow}>
                  <input
                    type="radio"
                    className={styles.questionChoiceRadio}
                    name={`correct-${selectedQuestion.id}`}
                    checked={selectedQuestion.correctChoiceIndex === cIndex}
                    onChange={() =>
                      updateQuestionAt(selectedQuestion.id, (q) => ({
                        ...q,
                        correctChoiceIndex: cIndex,
                      }))
                    }
                    disabled={saving}
                    title={t(
                      "pro.system.certification.training.questions.markCorrect",
                    )}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <LocalizedField
                      label={t(
                        "pro.system.certification.training.questions.choiceN",
                        { n: cIndex + 1 },
                      )}
                      locKey={choice.loc.key}
                      languages={languages}
                      selectedLang={activeLangFor(choice.loc.key)}
                      onSelectedLangChange={(lang) =>
                        onFieldLangChange(choice.loc.key, lang)
                      }
                      value={getLocDisplayValue(
                        choice.loc,
                        activeLangFor(choice.loc.key),
                        defaultLang,
                      )}
                      referenceValue={choice.loc.valuesByLang[defaultLang]}
                      onChange={(lang, value) =>
                        updateQuestionAt(selectedQuestion.id, (q) => ({
                          ...q,
                          choices: q.choices.map((c, i) =>
                            i === cIndex
                              ? { ...c, loc: setLocValue(c.loc, lang, value) }
                              : c,
                          ),
                        }))
                      }
                      onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
                      disabled={saving || !locReady}
                      placeholder={t(
                        "pro.system.certification.training.questions.choiceN",
                        { n: cIndex + 1 },
                      )}
                      defaultLang={defaultLang}
                      changed={choice.loc.dirtyLangs.length > 0}
                      flashKey={flashKeys[choice.loc.key] ?? 0}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      updateQuestionAt(selectedQuestion.id, (q) => {
                        if (q.choices.length <= 2) return q;
                        const nextChoices = q.choices.filter((_, i) => i !== cIndex);
                        let correct = q.correctChoiceIndex;
                        if (cIndex === correct) correct = 0;
                        else if (cIndex < correct) correct -= 1;
                        return {
                          ...q,
                          choices: nextChoices,
                          correctChoiceIndex: correct,
                        };
                      })
                    }
                    disabled={saving || selectedQuestion.choices.length <= 2}
                    title={t(
                      "pro.system.certification.training.questions.removeChoice",
                    )}
                    style={{
                      marginTop: 28,
                      opacity: selectedQuestion.choices.length <= 2 ? 0.4 : 1,
                      background: "transparent",
                      border: "none",
                      color: "inherit",
                      cursor:
                        selectedQuestion.choices.length <= 2
                          ? "not-allowed"
                          : "pointer",
                      padding: 4,
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                color="neutral"
                variant="ghost"
                size="sm"
                iconLeft={<Plus size={12} />}
                onClick={() =>
                  updateQuestionAt(selectedQuestion.id, (q) => {
                    const choiceId = newId();
                    return {
                      ...q,
                      choices: [
                        ...q.choices,
                        {
                          id: choiceId,
                          loc: {
                            key: questionChoiceKey(q.id, choiceId),
                            valuesByLang: { [defaultLang]: "" },
                            dirtyLangs: [],
                          },
                        },
                      ],
                    };
                  })
                }
                disabled={saving}
              >
                {t("pro.system.certification.training.questions.addChoice")}
              </Button>
            </div>
          </div>
        ) : (
          <p className={styles.questionEmpty}>
            {t("pro.system.certification.training.questions.empty")}
          </p>
        )}
      </div>
    </div>
  );
}
